import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Building2, CreditCard, Eye, EyeOff, KeyRound, ShieldCheck, Store, Users as UsersIcon } from "lucide-react";

export default function PlatformDashboard() {
  const { user, logout } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    (async () => {
      const [t, p] = await Promise.all([api.get("/admin/tenants"), api.get("/plans")]);
      setTenants(t.data || []); setPlans(p.data || []);
    })();
  }, []);

  const active = tenants.filter((t) => t.subscription?.status === "active").length;
  const trial = tenants.filter((t) => t.subscription?.status === "trial").length;
  const mrr = tenants.reduce((s, t) => s + (t.plan?.price_monthly || 0), 0);

  const changePassword = async (event) => {
    event.preventDefault();
    const { current_password, new_password, confirm_password } = passwordForm;
    if (new_password !== confirm_password) {
      toast.error("Konfirmasi password baru tidak sama");
      return;
    }
    if (new_password.length < 8) {
      toast.error("Password baru minimal 8 karakter");
      return;
    }
    setChangingPassword(true);
    try {
      const response = await api.post("/auth/change-password", passwordForm);
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      toast.success(response.data?.message || "Password berhasil diubah");
      window.setTimeout(() => logout(), 900);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Password gagal diubah");
    } finally {
      setChangingPassword(false);
    }
  };

  const passwordFields = [
    { key: "current_password", label: "Password Saat Ini", autoComplete: "current-password" },
    { key: "new_password", label: "Password Baru", autoComplete: "new-password" },
    { key: "confirm_password", label: "Konfirmasi Password Baru", autoComplete: "new-password" },
  ];

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-8">
        <div className="overline text-zinc-500">PLATFORM OWNER</div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Control Tower</h1>
        <p className="text-zinc-500 mt-1">Operasionalkan platform dan mitra Anda.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 grid-borders mb-10">
        {[
          { l: "Mitra Aktif", v: active, icon: Building2 },
          { l: "Trial Aktif", v: trial, icon: UsersIcon },
          { l: "Total Mitra", v: tenants.length, icon: Store },
          { l: "MRR Potensial", v: formatIDR(mrr), icon: CreditCard, raw: true },
        ].map((s) => (
          <div key={s.l} className="p-6 bg-white">
            <div className="flex justify-between items-start">
              <div className="overline text-zinc-500">{s.l}</div>
              <s.icon className="h-4 w-4 text-[#002FA7]"/>
            </div>
            <div className="mt-3 font-display font-black text-3xl lg:text-4xl tabular-nums">{s.raw?s.v:s.v}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-0 grid-borders">
        <div className="p-6 bg-white">
          <div className="overline text-zinc-500 mb-4">MITRA TERBARU</div>
          <div className="space-y-3">
            {tenants.slice(0,8).map((t) => (
              <div key={t.id} className="flex items-center justify-between border-b border-border pb-2">
                <div>
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-xs text-zinc-500">{t.outlets_count} outlet · {t.users_count} pengguna</div>
                </div>
                <div className="text-xs uppercase tracking-wider font-semibold">{t.subscription?.status || "-"}</div>
              </div>
            ))}
            {tenants.length===0 && <div className="text-sm text-zinc-500">Belum ada mitra.</div>}
          </div>
        </div>
        <div className="p-6 bg-white">
          <div className="overline text-zinc-500 mb-4">PAKET HARGA</div>
          <div className="space-y-3">
            {plans.map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-border pb-2">
                <div>
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-zinc-500">{p.max_outlets} outlet · {p.max_users} pengguna</div>
                </div>
                <div className="tabular-nums font-mono-num">{formatIDR(p.price_monthly)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="mt-10 border border-border bg-white" data-testid="owner-change-password-section">
        <div className="p-6 border-b border-border flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="overline text-zinc-500">KEAMANAN AKUN</div>
            <h2 className="font-display font-black text-2xl tracking-tighter mt-1">Ganti Password Owner</h2>
            <p className="text-sm text-zinc-500 mt-1">Akun: {user?.email || user?.phone || user?.name}</p>
          </div>
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2">
            <ShieldCheck className="h-4 w-4" /> Khusus Platform Owner
          </div>
        </div>

        <form onSubmit={changePassword} className="p-6 max-w-2xl space-y-4">
          {passwordFields.map((field) => (
            <div key={field.key}>
              <label className="overline text-zinc-600 block mb-1.5">{field.label}</label>
              <div className="relative">
                <input
                  type={showPasswords ? "text" : "password"}
                  autoComplete={field.autoComplete}
                  required
                  minLength={field.key === "current_password" ? 1 : 8}
                  maxLength={field.key === "current_password" ? 128 : 72}
                  value={passwordForm[field.key]}
                  onChange={(event) => setPasswordForm({ ...passwordForm, [field.key]: event.target.value })}
                  data-testid={`owner-${field.key.replaceAll("_", "-")}`}
                  className="w-full border border-border px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords((value) => !value)}
                  className="absolute right-0 top-0 h-full px-4 text-zinc-500 hover:text-zinc-900"
                  aria-label={showPasswords ? "Sembunyikan password" : "Tampilkan password"}
                >
                  {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          ))}

          <div className="text-xs leading-relaxed text-zinc-500 bg-zinc-50 border border-border p-3">
            Password baru minimal 8 karakter serta memiliki huruf besar, huruf kecil, dan angka. Setelah berhasil, seluruh sesi lama dibatalkan dan Anda harus login kembali.
          </div>

          <button
            type="submit"
            disabled={changingPassword}
            data-testid="owner-change-password-submit"
            className="inline-flex items-center justify-center gap-2 bg-zinc-900 text-white px-5 py-3 text-sm font-semibold hover:bg-[#002FA7] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <KeyRound className="h-4 w-4" />
            {changingPassword ? "Menyimpan…" : "Simpan Password Baru"}
          </button>
        </form>
      </section>
    </div>
  );
}
